<template>
  <PanelGroup class="flex-1">
    <div class="w-full flex flex-col h-full overflow-y-auto">
      <div class="flex items-center justify-between px-4 sm:px-12 py-8">
        <div class="flex gap-x-6 items-center">
          <div class="flex flex-col">
            <h2
              class="font-semibold text-blue-600"
              :class="[useLargeHeading ? 'text-lg sm:text-2xl' : 'text-lg']"
            >
              Activity Log
            </h2>
            <p
              class="text-xs font-semibold text-gray-700 flex items-center gap-0.5 capitalize"
              v-if="filters.resource_type && filters.resource_id"
            >
              <InfoIcon size="14" />
              {{ filters.resource_type }} {{ filters.resource_id }}
            </p>
          </div>
          <DataGridControlBar />
        </div>

        <div class="flex gap-x-2">
          <button
            class="btn"
            :class="[filters.status === FILTERS.ALL ? 'btn-primary' : 'btn-secondary-outline']"
            type="button"
            @click="filters.status = FILTERS.ALL"
          >
            All
          </button>
          <button
            type="button"
            class="btn"
            :class="[filters.status === FILTERS.SUCCESS ? 'btn-primary' : 'btn-secondary-outline']"
            @click="filters.status = FILTERS.SUCCESS"
          >
            Success
          </button>
          <button
            type="button"
            class="btn"
            :class="[filters.status === FILTERS.FAIL ? 'btn-primary' : 'btn-secondary-outline']"
            @click="filters.status = FILTERS.FAIL"
          >
            Fail
          </button>
        </div>
      </div>

      <ActivityLogTable
        name="product_activity_log"
        :filters="filters"
        :initial-cursor="urlParams.cursor"
        @rowClicked="onRowClicked"
      />
    </div>

    <template #right>
      <FlexPanel
        location="right"
        :open="Boolean(inspectedActivity)"
        :widths="inspectorPanelWidths"
        @esc-pressed="closeInspector"
        class="z-10 relative overflow-y-scroll shrink-0 bg-gray-100 md:shadow-xl md:rounded-tl-lg"
      >
        <div
          class="relative flex flex-col"
          v-if="inspectedActivity"
        >
          <PanelHeader
            :title="inspectedActivity.description"
            @close="closeInspector"
          />

          <div class="px-8 pt-2 pb-4">
            <div class="grid grid-cols-min-content_1fr gap-1">
              <template v-if="filters.resource_type === null && inspectedActivity.resource?.type">
                <div class="whitespace-nowrap font-semibold text-blue-600 pr-3">Resource</div>
                <div v-if="inspectedActivity.resource.url">
                  <Link :href="inspectedActivity.resource.url">{{ inspectedActivity.resource.title }}</Link>
                </div>
              </template>

              <template v-if="inspectedActivity.status">
                <div class="whitespace-nowrap font-semibold text-blue-600 pr-3">Status</div>
                <div
                  :class="[inspectedActivity.status.toLowerCase() === FILTERS.FAIL ? 'text-red-600' : 'text-green-600']"
                >
                  {{ inspectedActivity.status }}
                </div>
              </template>

              <div class="whitespace-nowrap font-semibold text-blue-600 pr-3">Time</div>
              <div>{{ formatTime(inspectedActivity.created_at) }}</div>

              <div class="whitespace-nowrap font-semibold text-blue-600 pr-3">Source</div>
              <div>{{ inspectedActivity.source?.display_name ?? 'N/A' }}</div>

              <div class="whitespace-nowrap font-semibold text-green-darkest pr-3">Initiator</div>
              <div>{{ inspectedActivity.user?.name ?? 'Salve' }}</div>
            </div>
          </div>

          <div class="px-8">
            <button
              class="btn flex items-center gap-x-1 w-40"
              :class="[copyState.wasCopied ? 'btn-success' : 'btn-primary']"
              @click="copyLinkToActivity"
            >
              <CheckIcon
                v-if="copyState.wasCopied"
                size="16"
              />
              <ClipboardCopyIcon
                v-else
                size="16"
              />
              <span v-if="!copyState.wasCopied">Copy link</span>
              <span v-else>Copied</span>
            </button>
          </div>

          <div
            v-if="inspectedActivity.properties"
            class="px-8 py-8"
          >
            <h3 class="font-semibold text-xl pb-2">Properties</h3>
            <JsonData
              :json="inspectedActivity.properties"
              :id="inspectedActivity.id"
            />
          </div>
        </div>
      </FlexPanel>
    </template>
  </PanelGroup>
</template>

<style scoped>
.grid-cols-min-content_1fr {
  grid-template-columns: min-content 1fr;
}
</style>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, computed, inject, nextTick } from 'vue'
import { PanelGroup, FlexPanel } from '@/Components/Panels'
import JsonData from '@/Components/JsonData.vue'
import ActivityLogTable from '@/Components/DataGrid/Tables/ActivityLog.vue'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'
import { formatTime } from '@/utils/datetime'
import { useGridState } from '@/Components/DataGrid/Composables/grid-state'
import { useLayoutState } from '@/Layouts/layout-state'
import { Link } from '@inertiajs/vue3'
import PanelHeader from '@/Components/Panels/PanelHeader.vue'
import { InfoIcon, ClipboardCopyIcon, CheckIcon } from 'lucide-vue-next'
import { useClipboard } from '@vueuse/core'
import { PAGE_URL_PARAMS } from '@/utils/symbols'

const urlParams = inject(PAGE_URL_PARAMS)

const pageLayout = useLayoutState()

onMounted(() => {
  pageLayout.setScrollLock(true)
})

onUnmounted(() => {
  pageLayout.setScrollLock(false)
  urlParams.cursor = null
  urlParams.inspect = null
})

const props = defineProps({
  resourceType: {
    type: [String, null],
    default: null
  },
  resourceId: {
    type: [Number, null],
    default: null
  },
  useLargeHeading: {
    type: Boolean,
    default: false
  }
})

const { context } = useGridState()
const gridApi = computed(() => context.gridApi)

const FILTERS = {
  ALL: null,
  SUCCESS: 'success',
  FAIL: 'fail'
}

const inspectorPanelWidths = [
  ['(max-width: 768px)', '100vw'],
  ['(min-width: 768px)', '600px']
]

const filters = reactive({
  resource_type: urlParams.resource_type ?? props.resourceType,
  resource_id: urlParams.resource_id ?? props.resourceId,
  status: urlParams.status === 'all' ? null : urlParams.status
})

watch(
  () => filters.status,
  (newStatus) => {
    urlParams.status = newStatus === null ? 'all' : newStatus
  }
)

watch(
  () => urlParams.cursor,
  () => {
    nextTick(() => {
      closeInspector()
    })
  }
)

const inspectedActivity = ref(null)

const openInspector = (activity) => {
  inspectedActivity.value = activity
}

const closeInspector = () => {
  inspectedActivity.value = null
  context.deselectSelectedRow()
  urlParams.inspect = null
}

const onRowClicked = (params) => {
  urlParams.inspect = params.data.id
  openInspector(params.data)
}

watch(
  gridApi,
  () => {
    gridApi.value.addEventListener('firstDataRendered', () => {
      if (urlParams.inspect) {
        const rowNode = gridApi.value.getRowNode(urlParams.inspect)
        if (!rowNode) return

        openInspector(rowNode.data)
        rowNode.setSelected(true)
        gridApi.value.ensureNodeVisible(rowNode)
      }
    })
  },
  { once: true }
)

const copyState = reactive({
  wasCopied: false,
  timer: null
})

const copyLinkToActivity = () => {
  const { copy, isSupported } = useClipboard({ source: window.location.href, legacy: true })

  if (!isSupported.value) {
    // @Note: this should never actually happen, especially since we're using "legacy".
    alert('Your browser does not support copying to clipboard.')
    return
  }

  copy()

  copyState.wasCopied = true

  clearTimeout(copyState.timer)

  copyState.timer = setTimeout(() => {
    copyState.wasCopied = false
  }, 2000)
}
</script>
